import { useEffect, useMemo, useState } from "react";
import { fetchRoles, attachRolesToUser } from "../../api/accessControl";

export default function AttachRolesModal({ user, onClose, onSuccess }) {
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);

  useEffect(() => {
    let active = true;

    const loadRoles = async () => {
      if (!user) return;

      try {
        setLoadingRoles(true);
        const data = await fetchRoles();
        if (!active) return;
        setRoles(data || []);
      } finally {
        if (active) setLoadingRoles(false);
      }
    };

    loadRoles();

    return () => {
      active = false;
    };
  }, [user]);

  const filteredRoles = useMemo(() => {
    if (!search.trim()) return roles;
    return roles.filter((r) =>
      r.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [roles, search]);

  const toggleRole = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (!user?.raw?.id || selected.length === 0) return;

    try {
      setLoading(true);
      await attachRolesToUser({
        userId: user.raw.id,
        roleIds: selected,
      });
      onSuccess?.();
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
      <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
              Attach Roles
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Assign access roles to{" "}
              <span className="font-medium text-slate-800 dark:text-slate-200">
                {user.name}
              </span>
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* Search */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Search roles
            </label>

            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
                🔎
              </span>

              <input
                placeholder="Search by role name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950/40">
            <span className="text-slate-600 dark:text-slate-400">
              {loadingRoles
                ? "Loading available roles..."
                : `${filteredRoles.length} role${
                    filteredRoles.length === 1 ? "" : "s"
                  } found`}
            </span>

            <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {selected.length} selected
            </span>
          </div>

          {/* Roles list */}
          <div className="max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-900">
            {loadingRoles ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700 dark:border-slate-700 dark:border-t-slate-200" />
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                  Fetching roles...
                </p>
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-xl dark:bg-slate-800">
                  📭
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  No roles found
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Try a different search term.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredRoles.map((r) => {
                  const isChecked = selected.includes(r.id);

                  return (
                    <label
                      key={r.id}
                      className={`flex cursor-pointer items-center justify-between rounded-2xl border px-4 py-3 transition ${
                        isChecked
                          ? "border-slate-900 bg-slate-50 dark:border-white dark:bg-slate-800/60"
                          : "border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-800 dark:hover:bg-slate-800/40"
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleRole(r.id)}
                          className="h-4 w-4 rounded border-slate-300 text-black focus:ring-2 focus:ring-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white dark:focus:ring-slate-700"
                        />

                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                            {r.name}
                          </p>

                          {r.description && (
                            <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                              {r.description}
                            </p>
                          )}
                        </div>
                      </div>

                      {isChecked && (
                        <span className="ml-3 shrink-0 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white dark:bg-white dark:text-black">
                          Selected
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={submit}
            disabled={loading || selected.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black"
          >
            {loading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent dark:border-black/70 dark:border-t-transparent" />
            )}
            {loading ? "Attaching..." : "Attach Roles"}
          </button>
        </div>
      </div>
    </div>
  );
}